/**
 * useSkillVersions.ts — fetch state for the skill-config version list
 * (story E0-BO-CFG-SKILL-REGISTRY-ADMIN + ACTIVATE-EXISTING-VERSION).
 *
 * Owns the request lifecycle for `GET /api/config/skills`, now a paginated/
 * filtered/sorted envelope (`{ items, total }`) — `total` backs the caller's
 * pagination controls. Race-safety mirrors useLeadsQuery: a monotonic
 * request id means only the LATEST `load` may commit, and a response
 * resolving after unmount is a no-op.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { UnauthorizedError, type ApiClient, type SkillVersionsListQuery } from '../../api/client';
import type { SkillVersionSummary } from '../../api/types';

export type SkillVersionsPhase = 'idle' | 'loading' | 'ready' | 'unauthorized' | 'error';

export interface SkillVersionsState {
  items: SkillVersionSummary[];
  total: number;
  phase: SkillVersionsPhase;
  load: (query: SkillVersionsListQuery) => Promise<void>;
}

export const useSkillVersions = (apiClient: ApiClient): SkillVersionsState => {
  const [items, setItems] = useState<SkillVersionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [phase, setPhase] = useState<SkillVersionsPhase>('idle');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (query: SkillVersionsListQuery) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const isCurrent = () => mountedRef.current && seq === requestSeq.current;
      setPhase('loading');
      try {
        const result = await apiClient.getSkillVersions(query);
        if (!isCurrent()) return;
        setItems(result.items);
        setTotal(result.total);
        setPhase('ready');
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setPhase('unauthorized');
          return;
        }
        setItems([]);
        setTotal(0);
        setPhase('error');
      }
    },
    [apiClient],
  );

  return { items, total, phase, load };
};
